import moment from 'moment-timezone';

const EPS = 1e-10;

const toRad = (d) => (d * Math.PI) / 180;

const haversineMeters = (a, b) => {
  const R = 6371000;
  const dLat = toRad((b.lat || 0) - (a.lat || 0));
  const dLng = toRad((b.lng || 0) - (a.lng || 0));
  const lat1 = toRad(a.lat || 0);
  const lat2 = toRad(b.lat || 0);

  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const parseEpochMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const n = Number(value);
    return n < 1e12 ? n * 1000 : n;
  }
  const m = moment(value);
  return m.isValid() ? m.valueOf() : null;
};

const parseLatLng = (row) => {
  if (typeof row.lat_long === 'string' && row.lat_long.includes(',')) {
    const [latStr, lngStr] = row.lat_long.split(',');
    const lat = Number(latStr.trim());
    const lng = Number(lngStr.trim());
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const lat = Number(row.latitude ?? row.lat ?? row.locationLat ?? row.location_lat);
  const lng = Number(row.longitude ?? row.lng ?? row.locationLng ?? row.location_lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };

  if (typeof row.location === 'string' && row.location.includes(',')) {
    const [latStr, lngStr] = row.location.split(',');
    const pLat = Number(latStr.trim());
    const pLng = Number(lngStr.trim());
    if (Number.isFinite(pLat) && Number.isFinite(pLng)) return { lat: pLat, lng: pLng };
  }

  return null;
};

export const extractRawRows = (iotData) => {
  if (!iotData) return [];
  if (Array.isArray(iotData)) return iotData;
  if (Array.isArray(iotData.deviceData)) return iotData.deviceData;
  if (Array.isArray(iotData.raw)) return iotData.raw;
  if (Array.isArray(iotData.data)) return iotData.data;
  if (Array.isArray(iotData.rows)) return iotData.rows;
  if (Array.isArray(iotData.records)) return iotData.records;
  if (iotData.payload && Array.isArray(iotData.payload)) return iotData.payload;
  return [];
};

export const normalizeRawPoints = (rawRows) => {
  const points = rawRows
    .map((row, idx) => {
      const ts = parseEpochMs(
        row.timestamp ?? row.ts ?? row.time ?? row.datetime_ist ?? row.datetime ?? row.incoming_time ?? row.incoming_datetime_ist
      );
      const loc = parseLatLng(row);
      if (!ts || !loc) return null;

      const ignition = Number(
        row.calculated_ignition ?? row.effective_ignition ?? row.ignition ?? row.dig_ign ?? 0
      ) > 0 ? 1 : 0;

      const speed = Number(row.speed_gnss ?? row.speed ?? row.velocity ?? 0) || 0;

      const odometer = Number(row.odometer_kms ?? row.odometer ?? row.distance ?? 0) || 0;
      const idle = Number(
        row.calculated_idle ?? row.effective_idle ?? row.idle ?? row.calculatedIdle ?? 0
      ) > 0 ? 1 : 0;

      return {
        id: row._id || `p_${idx}_${ts}`,
        ts,
        lat: loc.lat,
        lng: loc.lng,
        ignition,
        idle,
        speed,
        odometer,
        raw: row,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.ts - b.ts);

  return points;
};

const pointOnSegment = (p, a, b) => {
  const lenSq = (b.lng - a.lng) ** 2 + (b.lat - a.lat) ** 2;
  // Ignore degenerate edges (common in closed rings where first==last),
  // except when point equals the vertex.
  if (lenSq <= EPS) {
    return Math.abs(p.lng - a.lng) <= EPS && Math.abs(p.lat - a.lat) <= EPS;
  }

  const cross = (b.lng - a.lng) * (p.lat - a.lat) - (b.lat - a.lat) * (p.lng - a.lng);
  if (Math.abs(cross) > EPS) return false;
  const dot = (p.lng - a.lng) * (b.lng - a.lng) + (p.lat - a.lat) * (b.lat - a.lat);
  if (dot < 0) return false;
  return dot <= lenSq + EPS;
};

const polygonAreaAbs = (polygon) => {
  if (!Array.isArray(polygon) || polygon.length < 3) return Number.POSITIVE_INFINITY;
  let sum = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const j = (i + 1) % polygon.length;
    sum += (polygon[i].lng * polygon[j].lat) - (polygon[j].lng * polygon[i].lat);
  }
  return Math.abs(sum / 2);
};

const polygonCentroid = (polygon) => {
  if (!Array.isArray(polygon) || polygon.length === 0) return { lat: 0, lng: 0 };
  if (polygon.length < 3) return { lat: polygon[0].lat, lng: polygon[0].lng };

  let areaTwice = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const j = (i + 1) % polygon.length;
    const cross = (polygon[i].lng * polygon[j].lat) - (polygon[j].lng * polygon[i].lat);
    areaTwice += cross;
    cx += (polygon[i].lng + polygon[j].lng) * cross;
    cy += (polygon[i].lat + polygon[j].lat) * cross;
  }

  if (Math.abs(areaTwice) <= EPS) {
    const sum = polygon.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
    return { lat: sum.lat / polygon.length, lng: sum.lng / polygon.length };
  }

  return {
    lng: cx / (3 * areaTwice),
    lat: cy / (3 * areaTwice),
  };
};

export const isPointInPolygon = (point, polygon) => {
  if (!Array.isArray(polygon) || polygon.length < 3) return false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    if (pointOnSegment(point, polygon[i], polygon[j])) return true;
  }

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;

    const intersect = yi > point.lat !== yj > point.lat
      && point.lng < ((xj - xi) * (point.lat - yi)) / ((yj - yi) || EPS) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const polygonCandidatesForPoint = (point, zones, areaByZoneId = {}) => {
  const candidates = [];
  for (const zone of zones) {
    if (!isPointInPolygon(point, zone.coordinates || [])) continue;
    candidates.push({
      zoneId: zone.id,
      area: areaByZoneId[zone.id] ?? polygonAreaAbs(zone.coordinates || []),
    });
  }

  candidates.sort((a, b) => {
    if (a.area !== b.area) return a.area - b.area;
    return `${a.zoneId}`.localeCompare(`${b.zoneId}`);
  });
  return candidates;
};

const zoneForPoint = (point, zones, areaByZoneId = {}) => {
  const candidates = polygonCandidatesForPoint(point, zones, areaByZoneId);
  return candidates.length > 0 ? candidates[0].zoneId : null;
};

const eventFactory = (type, base) => ({
  id: `${type}_${base.assetId}_${base.startTs}_${Math.random().toString(36).slice(2, 8)}`,
  eventType: type,
  ...base,
});

const findNearestZoneByCentroid = (point, zones, centroidByZoneId) => {
  if (!Array.isArray(zones) || zones.length === 0) return null;
  const byDistance = zones
    .map((zone) => ({
      zoneId: zone.id,
      distanceMeters: haversineMeters(point, centroidByZoneId[zone.id] || { lat: 0, lng: 0 }),
    }))
    .sort((a, b) => {
      if (Math.abs(a.distanceMeters - b.distanceMeters) > 1e-6) return a.distanceMeters - b.distanceMeters;
      return `${a.zoneId}`.localeCompare(`${b.zoneId}`);
    });

  if (byDistance.length === 0) return null;
  const nearest = byDistance[0];
  const tieToleranceMeters = 0.5;
  const candidateZoneCount = byDistance.filter((z) => Math.abs(z.distanceMeters - nearest.distanceMeters) <= tieToleranceMeters).length;

  return {
    zoneId: nearest.zoneId,
    distanceMeters: nearest.distanceMeters,
    candidateZoneCount,
  };
};

const valueAsString = (v) => (v === null || v === undefined ? '' : `${v}`.trim());

const firstPresentValue = (values) => {
  for (const value of values) {
    const str = valueAsString(value);
    if (str) return str;
  }
  return '';
};

const resolveFromCutoffMap = (map, keys) => {
  if (!map || typeof map !== 'object') return null;
  for (const key of keys) {
    if (!key) continue;
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      const n = Number(map[key]);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }

  const lowerMap = {};
  Object.keys(map).forEach((k) => {
    lowerMap[k.toLowerCase()] = map[k];
  });

  for (const key of keys) {
    if (!key) continue;
    const maybe = lowerMap[key.toLowerCase()];
    const n = Number(maybe);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const resolveAssetTypeContext = (asset) => {
  const assetTypeKey = firstPresentValue([
    asset?.assetType?._id,
    asset?.assetType?.id,
    asset?.assetType?.name,
    asset?.asset_type?._id,
    asset?.asset_type?.id,
    asset?.asset_type?.name,
    asset?.assetTypeId,
    asset?.assetTypeName,
    asset?.assetType,
    asset?.type,
  ]);

  const assetSubTypeKey = firstPresentValue([
    asset?.assetSubType?._id,
    asset?.assetSubType?.id,
    asset?.assetSubType?.name,
    asset?.asset_sub_type?._id,
    asset?.asset_sub_type?.id,
    asset?.asset_sub_type?.name,
    asset?.assetSubTypeId,
    asset?.assetSubTypeName,
    asset?.assetSubType,
    asset?.subType,
  ]);

  return {
    assetTypeKey,
    assetSubTypeKey,
  };
};

const resolveProximityCutoffMeters = (rules, assetContext) => {
  const defaultCutoff = Math.max(1, Number(rules.defaultProximityCutoffMeters || 30));
  const subTypeCutoff = resolveFromCutoffMap(rules.proximityByAssetSubType, [assetContext.assetSubTypeKey]);
  if (subTypeCutoff) return subTypeCutoff;

  const typeCutoff = resolveFromCutoffMap(rules.proximityByAssetType, [assetContext.assetTypeKey]);
  if (typeCutoff) return typeCutoff;

  return defaultCutoff;
};

const toServicePayload = ({ nearest, cutoffMeters, exitThresholdMeters, assetContext, extras = {} }) => ({
  detectionMode: 'proximity',
  distanceMeters: Number(((nearest?.distanceMeters ?? 0)).toFixed(2)),
  cutoffMeters,
  exitThresholdMeters: Number(exitThresholdMeters.toFixed(2)),
  assetTypeKey: assetContext.assetSubTypeKey || assetContext.assetTypeKey || '',
  candidateZoneCount: nearest?.candidateZoneCount || 1,
  ...extras,
});

const deriveZoneEvents = (points, template, asset, dateKey) => {
  const zones = template.zones || [];
  const zoneNameById = {};
  const zoneAreaById = {};
  const zoneCentroidById = {};
  zones.forEach((zone) => {
    zoneNameById[zone.id] = zone.name || zone.id;
    zoneAreaById[zone.id] = polygonAreaAbs(zone.coordinates || []);
    zoneCentroidById[zone.id] = polygonCentroid(zone.coordinates || []);
  });

  const rules = template.rules || {};
  const debouncePoints = Math.max(1, Number(rules.debouncePoints || 2));
  const sampleIntervalSec = Math.max(1, Number(rules.sampleIntervalSec || 10));
  const maxGapSec = sampleIntervalSec * Math.max(1, Number(rules.maxGapSamples || 3));
  const dwellThresholdSec = Math.max(0, Number(rules.dwellThresholdSec || 120));

  const proximityEnabled = !!rules.proximityEnabled;
  const proximityExitHysteresisPct = Math.max(0, Number(rules.proximityExitHysteresisPct || 20));
  const assetContext = resolveAssetTypeContext(asset);
  const cutoffMeters = resolveProximityCutoffMeters(rules, assetContext);
  const exitThresholdMeters = cutoffMeters * (1 + proximityExitHysteresisPct / 100);

  if (points.length === 0) return [];

  const baseEvent = {
    templateId: template.id,
    orgId: template.orgId,
    assetId: asset._id,
    assetCode: asset.assetCode,
    date: dateKey,
  };

  const polygonEvents = [];
  const serviceEvents = [];
  const gapEvents = [];

  const firstPolygonZone = zoneForPoint(points[0], zones, zoneAreaById);
  const firstNearest = !firstPolygonZone && proximityEnabled
    ? findNearestZoneByCentroid(points[0], zones, zoneCentroidById)
    : null;
  const firstServiceZone = firstNearest && firstNearest.distanceMeters <= cutoffMeters ? firstNearest.zoneId : null;

  let stablePolygonZone = firstPolygonZone;
  let stableServiceZone = firstServiceZone;

  let polygonCandidate = null;
  let polygonCandidateCount = 0;
  let polygonCandidateTs = null;

  let serviceCandidate = null;
  let serviceCandidateCount = 0;
  let serviceCandidateTs = null;
  let serviceCandidateMeta = null;

  let currentPolygonEnter = stablePolygonZone
    ? { zoneId: stablePolygonZone, ts: points[0].ts, uncertain: false }
    : null;

  let currentServiceEnter = stableServiceZone
    ? { zoneId: stableServiceZone, ts: points[0].ts, uncertain: false, nearest: firstNearest }
    : null;

  if (stablePolygonZone) {
    polygonEvents.push(eventFactory('ENTER', {
      ...baseEvent,
      zoneId: stablePolygonZone,
      zoneName: zoneNameById[stablePolygonZone] || stablePolygonZone,
      startTs: points[0].ts,
      payloadJson: { debounceApplied: false, uncertainGapBefore: false },
    }));
  }

  if (stableServiceZone) {
    serviceEvents.push(eventFactory('SERVICE_ENTER', {
      ...baseEvent,
      zoneId: stableServiceZone,
      zoneName: zoneNameById[stableServiceZone] || stableServiceZone,
      startTs: points[0].ts,
      payloadJson: toServicePayload({
        nearest: firstNearest,
        cutoffMeters,
        exitThresholdMeters,
        assetContext,
        extras: {
          debounceApplied: false,
          uncertainGapBefore: false,
        },
      }),
    }));
  }

  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];
    const prev = points[i - 1];
    const polygonZone = zoneForPoint(point, zones, zoneAreaById);

    const gapSec = (point.ts - prev.ts) / 1000;
    const uncertainGap = gapSec > maxGapSec;

    if (uncertainGap) {
      const activeZone = stablePolygonZone || stableServiceZone || null;
      gapEvents.push(eventFactory('GAP', {
        ...baseEvent,
        zoneId: activeZone,
        zoneName: activeZone ? (zoneNameById[activeZone] || activeZone) : null,
        startTs: prev.ts,
        endTs: point.ts,
        durationSec: gapSec,
        payloadJson: { reason: 'MISSING_SAMPLES', maxGapSec },
      }));
    }

    if (polygonZone === stablePolygonZone) {
      polygonCandidate = null;
      polygonCandidateCount = 0;
      polygonCandidateTs = null;
    } else {
      if (polygonZone === polygonCandidate) {
        polygonCandidateCount += 1;
      } else {
        polygonCandidate = polygonZone;
        polygonCandidateCount = 1;
        polygonCandidateTs = point.ts;
      }

      if (polygonCandidateCount >= debouncePoints) {
        const transitionTs = polygonCandidateTs || point.ts;

        if (stablePolygonZone) {
          polygonEvents.push(eventFactory('EXIT', {
            ...baseEvent,
            zoneId: stablePolygonZone,
            zoneName: zoneNameById[stablePolygonZone] || stablePolygonZone,
            startTs: transitionTs,
            payloadJson: { debounceApplied: true, uncertainGapBefore: uncertainGap },
          }));

          if (currentPolygonEnter && currentPolygonEnter.zoneId === stablePolygonZone) {
            const durationSec = Math.max(0, Math.round((transitionTs - currentPolygonEnter.ts) / 1000));
            if (durationSec >= dwellThresholdSec) {
              polygonEvents.push(eventFactory('DWELL', {
                ...baseEvent,
                zoneId: stablePolygonZone,
                zoneName: zoneNameById[stablePolygonZone] || stablePolygonZone,
                startTs: currentPolygonEnter.ts,
                endTs: transitionTs,
                durationSec,
                payloadJson: {
                  thresholdSec: dwellThresholdSec,
                  uncertain: currentPolygonEnter.uncertain || uncertainGap,
                },
              }));
            }
          }
        }

        stablePolygonZone = polygonCandidate;
        if (stablePolygonZone) {
          polygonEvents.push(eventFactory('ENTER', {
            ...baseEvent,
            zoneId: stablePolygonZone,
            zoneName: zoneNameById[stablePolygonZone] || stablePolygonZone,
            startTs: transitionTs,
            payloadJson: { debounceApplied: true, uncertainGapBefore: uncertainGap },
          }));
          currentPolygonEnter = { zoneId: stablePolygonZone, ts: transitionTs, uncertain: uncertainGap };
        } else {
          currentPolygonEnter = null;
        }

        polygonCandidate = null;
        polygonCandidateCount = 0;
        polygonCandidateTs = null;
      }
    }

    let serviceObservedZone = null;
    let nearest = null;

    if (!polygonZone && proximityEnabled) {
      nearest = findNearestZoneByCentroid(point, zones, zoneCentroidById);

      if (stableServiceZone) {
        const stableCentroid = zoneCentroidById[stableServiceZone];
        const distToStable = stableCentroid ? haversineMeters(point, stableCentroid) : Number.POSITIVE_INFINITY;

        if (distToStable <= exitThresholdMeters) {
          serviceObservedZone = stableServiceZone;
          nearest = {
            zoneId: stableServiceZone,
            distanceMeters: distToStable,
            candidateZoneCount: nearest?.candidateZoneCount || 1,
          };
        } else if (nearest && nearest.distanceMeters <= cutoffMeters) {
          serviceObservedZone = nearest.zoneId;
        } else {
          serviceObservedZone = null;
        }
      } else if (nearest && nearest.distanceMeters <= cutoffMeters) {
        serviceObservedZone = nearest.zoneId;
      }
    }

    if (serviceObservedZone === stableServiceZone) {
      serviceCandidate = null;
      serviceCandidateCount = 0;
      serviceCandidateTs = null;
      serviceCandidateMeta = null;
      continue;
    }

    if (serviceObservedZone === serviceCandidate) {
      serviceCandidateCount += 1;
    } else {
      serviceCandidate = serviceObservedZone;
      serviceCandidateCount = 1;
      serviceCandidateTs = point.ts;
      serviceCandidateMeta = nearest;
    }

    if (serviceCandidateCount >= debouncePoints) {
      const transitionTs = serviceCandidateTs || point.ts;
      const transitionNearest = serviceCandidateMeta || nearest;

      if (stableServiceZone) {
        serviceEvents.push(eventFactory('SERVICE_EXIT', {
          ...baseEvent,
          zoneId: stableServiceZone,
          zoneName: zoneNameById[stableServiceZone] || stableServiceZone,
          startTs: transitionTs,
          payloadJson: toServicePayload({
            nearest: transitionNearest,
            cutoffMeters,
            exitThresholdMeters,
            assetContext,
            extras: {
              debounceApplied: true,
              uncertainGapBefore: uncertainGap,
            },
          }),
        }));

        if (currentServiceEnter && currentServiceEnter.zoneId === stableServiceZone) {
          const durationSec = Math.max(0, Math.round((transitionTs - currentServiceEnter.ts) / 1000));
          if (durationSec >= dwellThresholdSec) {
            serviceEvents.push(eventFactory('SERVICE_DWELL', {
              ...baseEvent,
              zoneId: stableServiceZone,
              zoneName: zoneNameById[stableServiceZone] || stableServiceZone,
              startTs: currentServiceEnter.ts,
              endTs: transitionTs,
              durationSec,
              payloadJson: toServicePayload({
                nearest: currentServiceEnter.nearest,
                cutoffMeters,
                exitThresholdMeters,
                assetContext,
                extras: {
                  thresholdSec: dwellThresholdSec,
                  uncertain: currentServiceEnter.uncertain || uncertainGap,
                },
              }),
            }));
          }
        }
      }

      stableServiceZone = serviceCandidate;
      if (stableServiceZone) {
        serviceEvents.push(eventFactory('SERVICE_ENTER', {
          ...baseEvent,
          zoneId: stableServiceZone,
          zoneName: zoneNameById[stableServiceZone] || stableServiceZone,
          startTs: transitionTs,
          payloadJson: toServicePayload({
            nearest: transitionNearest,
            cutoffMeters,
            exitThresholdMeters,
            assetContext,
            extras: {
              debounceApplied: true,
              uncertainGapBefore: uncertainGap,
            },
          }),
        }));

        currentServiceEnter = {
          zoneId: stableServiceZone,
          ts: transitionTs,
          uncertain: uncertainGap,
          nearest: transitionNearest,
        };
      } else {
        currentServiceEnter = null;
      }

      serviceCandidate = null;
      serviceCandidateCount = 0;
      serviceCandidateTs = null;
      serviceCandidateMeta = null;
    }
  }

  const lastPointTs = points[points.length - 1].ts;

  if (currentPolygonEnter && currentPolygonEnter.zoneId) {
    const durationSec = Math.max(0, Math.round((lastPointTs - currentPolygonEnter.ts) / 1000));
    if (durationSec >= dwellThresholdSec) {
      polygonEvents.push(eventFactory('DWELL', {
        ...baseEvent,
        zoneId: currentPolygonEnter.zoneId,
        zoneName: zoneNameById[currentPolygonEnter.zoneId] || currentPolygonEnter.zoneId,
        startTs: currentPolygonEnter.ts,
        endTs: lastPointTs,
        durationSec,
        payloadJson: { thresholdSec: dwellThresholdSec, openInterval: true },
      }));
    }
  }

  if (currentServiceEnter && currentServiceEnter.zoneId) {
    const durationSec = Math.max(0, Math.round((lastPointTs - currentServiceEnter.ts) / 1000));
    if (durationSec >= dwellThresholdSec) {
      serviceEvents.push(eventFactory('SERVICE_DWELL', {
        ...baseEvent,
        zoneId: currentServiceEnter.zoneId,
        zoneName: zoneNameById[currentServiceEnter.zoneId] || currentServiceEnter.zoneId,
        startTs: currentServiceEnter.ts,
        endTs: lastPointTs,
        durationSec,
        payloadJson: toServicePayload({
          nearest: currentServiceEnter.nearest,
          cutoffMeters,
          exitThresholdMeters,
          assetContext,
          extras: {
            thresholdSec: dwellThresholdSec,
            openInterval: true,
          },
        }),
      }));
    }
  }

  return [...polygonEvents, ...serviceEvents, ...gapEvents].sort((a, b) => a.startTs - b.startTs);
};

const buildTripEvents = (zoneEvents, template, asset, dateKey, points) => {
  const zoneNameById = {};
  (template.zones || []).forEach((zone) => {
    zoneNameById[zone.id] = zone.name || zone.id;
  });
  const rules = template.rules || {};
  const maxTripSec = Math.max(60, Number(rules.tripMaxDurationSec || 8 * 60 * 60));

  const metrics = template.metrics || [];
  const tripMetrics = metrics.filter((m) => m.type === 'trip_count');
  if (tripMetrics.length === 0) return [];

  const events = [];
  const sorted = zoneEvents.filter((e) => e.eventType === 'ENTER' || e.eventType === 'EXIT').sort((a, b) => a.startTs - b.startTs);

  tripMetrics.forEach((metric) => {
    const source = metric.config?.sourceZoneId;
    const destination = metric.config?.destinationZoneId;
    if (!source || !destination) return;

    let pendingExit = null;

    sorted.forEach((ev) => {
      if (ev.eventType === 'EXIT' && ev.zoneId === source) {
        pendingExit = ev;
        return;
      }

      if (ev.eventType === 'ENTER' && ev.zoneId === destination && pendingExit) {
        const durationSec = Math.round((ev.startTs - pendingExit.startTs) / 1000);
        if (durationSec > 0 && durationSec <= maxTripSec) {
          const tripDistance = estimateDistance(points, pendingExit.startTs, ev.startTs);
          events.push(eventFactory('TRIP', {
            templateId: template.id,
            orgId: template.orgId,
            assetId: asset._id,
            assetCode: asset.assetCode,
            date: dateKey,
            zoneId: destination,
            zoneName: zoneNameById[destination] || destination,
            startTs: pendingExit.startTs,
            endTs: ev.startTs,
            durationSec,
            payloadJson: {
              sourceZoneId: source,
              sourceZoneName: zoneNameById[source] || source,
              destinationZoneId: destination,
              destinationZoneName: zoneNameById[destination] || destination,
              metricId: metric.id,
              metricLabel: metric.label,
              distanceMeters: Math.round(tripDistance),
              valid: true,
            },
          }));
          pendingExit = null;
        }
      }
    });
  });

  return events;
};

const estimateDistance = (points, startTs, endTs) => {
  const path = points.filter((p) => p.ts >= startTs && p.ts <= endTs);
  if (path.length < 2) return 0;
  let dist = 0;
  for (let i = 1; i < path.length; i += 1) {
    dist += haversineMeters(path[i - 1], path[i]);
  }
  return dist;
};

const zoneTimeMap = (events) => {
  const map = {};
  events
    .filter((e) => e.eventType === 'DWELL')
    .forEach((e) => {
      map[e.zoneId] = (map[e.zoneId] || 0) + (Number(e.durationSec) || 0);
    });
  return map;
};

const metricKey = (metric) => metric.id || `${metric.type}_${metric.label || 'metric'}`;

const countRoundTrips = (tripEvents, sourceZoneId, destinationZoneId) => {
  const ordered = tripEvents
    .filter((e) => e.payloadJson?.sourceZoneId === sourceZoneId || e.payloadJson?.sourceZoneId === destinationZoneId)
    .sort((a, b) => a.startTs - b.startTs);

  let count = 0;
  let awaitingReturn = false;

  ordered.forEach((trip) => {
    const src = trip.payloadJson?.sourceZoneId;
    const dst = trip.payloadJson?.destinationZoneId;

    if (!awaitingReturn && src === sourceZoneId && dst === destinationZoneId) {
      awaitingReturn = true;
      return;
    }

    if (awaitingReturn && src === destinationZoneId && dst === sourceZoneId) {
      count += 1;
      awaitingReturn = false;
    }
  });

  return count;
};

const computeBaseToMultiPoi = (zoneEvents, baseZoneId) => {
  const ordered = zoneEvents
    .filter((e) => e.eventType === 'ENTER' || e.eventType === 'EXIT')
    .sort((a, b) => a.startTs - b.startTs);

  let active = false;
  let visited = new Set();
  let count = 0;

  ordered.forEach((event) => {
    if (!baseZoneId) return;

    if (!active && event.eventType === 'EXIT' && event.zoneId === baseZoneId) {
      active = true;
      visited = new Set();
      return;
    }

    if (!active) return;

    if (event.eventType === 'ENTER' && event.zoneId && event.zoneId !== baseZoneId) {
      visited.add(event.zoneId);
      return;
    }

    if (event.eventType === 'ENTER' && event.zoneId === baseZoneId) {
      if (visited.size >= 2) count += 1;
      active = false;
      visited = new Set();
    }
  });

  return count;
};

const getCarryMetric = (type, contextMetrics) => {
  if (type === 'carry_ignition_time') return contextMetrics.ignitionTimeSec || 0;
  if (type === 'carry_distance_run') return contextMetrics.distanceKm || 0;
  if (type === 'carry_fuel_consumed') return contextMetrics.fuelConsumed || 0;
  return 0;
};

export const computeAssetUtilization = ({ template, asset, points, contextMetrics, dateKey }) => {
  const zoneEvents = deriveZoneEvents(points, template, asset, dateKey);
  const polygonEvents = zoneEvents.filter((e) => ['ENTER', 'EXIT', 'DWELL'].includes(e.eventType));
  const tripEvents = buildTripEvents(polygonEvents, template, asset, dateKey, points);
  const allEvents = [...zoneEvents, ...tripEvents].sort((a, b) => a.startTs - b.startTs);

  const metrics = template.metrics || [];
  const zoneDurations = zoneTimeMap(polygonEvents);

  const entersByZone = {};
  const exitsByZone = {};
  polygonEvents.forEach((event) => {
    if (event.eventType === 'ENTER') entersByZone[event.zoneId] = (entersByZone[event.zoneId] || 0) + 1;
    if (event.eventType === 'EXIT') exitsByZone[event.zoneId] = (exitsByZone[event.zoneId] || 0) + 1;
  });

  const baseZoneId = (template.zones || []).find((z) => z.isBase)?.id || null;
  const totalWindowSec = points.length > 1 ? Math.max(0, Math.round((points[points.length - 1].ts - points[0].ts) / 1000)) : 0;

  const facts = metrics.map((metric) => {
    const key = metricKey(metric);
    const cfg = metric.config || {};
    let value = 0;
    let unit = 'count';

    switch (metric.type) {
      case 'zone_entries':
        value = entersByZone[cfg.zoneId] || 0;
        unit = 'count';
        break;
      case 'zone_exits':
        value = exitsByZone[cfg.zoneId] || 0;
        unit = 'count';
        break;
      case 'reentry_count':
        value = Math.max(0, (entersByZone[cfg.zoneId] || 0) - 1);
        unit = 'count';
        break;
      case 'time_inside':
        value = zoneDurations[cfg.zoneId] || 0;
        unit = 'sec';
        break;
      case 'time_outside_base': {
        const b = cfg.baseZoneId || baseZoneId;
        const inside = b ? (zoneDurations[b] || 0) : 0;
        value = Math.max(0, totalWindowSec - inside);
        unit = 'sec';
        break;
      }
      case 'trip_count':
        value = tripEvents.filter((t) => t.payloadJson?.metricId === metric.id).length;
        unit = 'count';
        break;
      case 'round_trip_count':
        value = countRoundTrips(tripEvents, cfg.sourceZoneId, cfg.destinationZoneId);
        unit = 'count';
        break;
      case 'base_to_multi_poi_visits':
        value = computeBaseToMultiPoi(polygonEvents, cfg.baseZoneId || baseZoneId);
        unit = 'count';
        break;
      case 'unique_poi_count': {
        const excludeBase = cfg.excludeBase !== false;
        const entered = new Set(polygonEvents.filter((e) => e.eventType === 'ENTER').map((e) => e.zoneId));
        if (excludeBase && baseZoneId) entered.delete(baseZoneId);
        value = entered.size;
        unit = 'count';
        break;
      }
      case 'carry_ignition_time':
      case 'carry_distance_run':
      case 'carry_fuel_consumed':
        value = getCarryMetric(metric.type, contextMetrics || {});
        unit = metric.type === 'carry_ignition_time' ? 'sec' : metric.type === 'carry_distance_run' ? 'km' : 'ltr';
        break;
      default:
        value = 0;
        unit = 'count';
    }

    return {
      id: `fact_${template.id}_${dateKey}_${asset._id}_${key}`,
      templateId: template.id,
      orgId: template.orgId,
      assetId: asset._id,
      assetCode: asset.assetCode,
      date: dateKey,
      metricKey: key,
      metricType: metric.type,
      metricLabel: metric.label,
      metricValue: Number.isFinite(value) ? value : 0,
      unit,
      comparisonPrevDay: null,
      payloadJson: {
        config: cfg,
      },
    };
  });

  return {
    events: allEvents,
    facts,
  };
};

export const toDateKeyInTz = (dateLike, timezone) => {
  return moment.tz(dateLike, timezone).format('YYYY-MM-DD');
};

export const toUtcRangeForDateInTz = (dateLike, timezone) => {
  const start = moment.tz(dateLike, timezone).startOf('day');
  const end = moment.tz(dateLike, timezone).endOf('day');
  return {
    startTs: start.clone().utc().valueOf(),
    endTs: end.clone().utc().valueOf(),
    dateKey: start.format('YYYY-MM-DD'),
  };
};
